<template>
  <fullscreen-dialog
    v-model="model"
    :title="dialogTitle"
    :subtitle="editedItem && editedItem.id ? 'ID: ' + editedItem.id : undefined"
    icon="mdi-file-document-outline"
    color="blue"
    :save-text="$t('order')"
    :disabled="!valid || !editedItem.groceries.length"
    :loading="loading"
    @save="save"
  >
    <template v-slot:app-bar-actions>
      <excel-button class="mr-2" @click="exportExcel" />
      <warehouse-button-print-menu @print="print" />
    </template>

    <v-form ref="form" v-model="valid">
      <v-row align="center">
        <v-col cols="12" lg="3" xl="2">
          <choose-warehouse-field
            v-model="editedItem.warehouse"
            return-object
            hide-details
            color="white"
            disabled
          />
        </v-col>
        <v-col cols="12" lg="2" xl="2">
          <date-picker
            v-model="editedItem.document_date"
            single-date
            color="white"
            disabled
          />
        </v-col>
        <v-col cols="12" lg="2" xl="2">
          <text-field
            :label="$t('warehouse.document_number_short')"
            :value="editedItem.number"
            hide-details
            color="white"
            readonly
            disabled
          />
        </v-col>
        <v-col cols="12" lg="3" xl="2">
          <text-field
            :label="$t('supplier')"
            :value="supplierName"
            hide-details
            color="white"
            readonly
            disabled
          />
        </v-col>
        <v-col cols="12" lg="2" xl="2">
          <text-field
            :label="$t('warehouse.supplier_invoice_number')"
            :value="editedItem.supplier_invoice_number"
            hide-details
            color="white"
            readonly
            disabled
          />
        </v-col>
        <v-col cols="auto">
          <v-checkbox
            v-model="showStock"
            :label="$t('warehouse.warehouse_status')"
            hide-details
            class="mt-0"
          />
        </v-col>
      </v-row>
    </v-form>

    <warehouse-document-groceries-table
      v-if="selectedWarehouse"
      :groceries="editedItem.groceries"
      :warehouse="selectedWarehouse"
      waybill
      :headers="showStock ? headersOfferInvoice : headersOfferInvoice.filter((h) => h.value !== 'warehouse_stock')"
      :title-text="$t('goods')"
      disabled
      @removeGrocery="removeGrocery"
    />

    <v-row class="mt-2">
      <v-col cols="12" md="6">
        <text-area
          :label="$t('note')"
          :value="editedItem.notes"
          rows="2"
          color="white"
          readonly
          disabled
        />
      </v-col>
    </v-row>
  </fullscreen-dialog>
</template>

<script>
import hasLastStocktaking from "@/mixins/warehouse/hasLastStocktaking";
import hasWarehouseDocumentDialog from "@/mixins/warehouse/hasWarehouseDocumentDialog";
import hasWarehousePrinting from "@/mixins/warehouse/hasWarehousePrinting";
import hasPrintingV2 from "@/mixins/hasPrintingV2";
import hasExcel from "@/mixins/hasExcel";
import { waybillDocumentGrocery } from "@/models/warehouse";

export default {
  name: "WarehouseOfferInvoiceDialog",
  mixins: [hasWarehouseDocumentDialog, hasLastStocktaking, hasPrintingV2, hasWarehousePrinting, hasExcel],
  props: {
    editedItem: {
      type: Object,
      default: waybillDocumentGrocery,
    },
  },
  data() {
    return {
      showStock: false,
      fullSupplier: null,
    };
  },
  computed: {
    dialogTitle() {
      if (this.editedItem?.document_type === "invoice") return this.$t("warehouse.invoice");
      if (this.editedItem?.document_type === "offer") return this.$t("warehouse.offer");
      return this.$t("warehouse.offers_invoices");
    },
    supplierName() {
      return this.toLatin(this.editedItem?.supplier?.name || "");
    },

    headersOfferInvoice() {
      return [
        { text: this.$t("r_b"), value: "num", width: "100px" },
        { text: this.$t("code"), value: "code", width: "120px" },
        { text: this.$t("name"), value: "name", width: "200px" },
        {
          text: this.$t("quantity"),
          value: "amount",
          align: "right",
          width: "200px",
        },
        {
          text: this.$t("price"),
          value: "unit_price",
          align: "right",
          width: "140px",
        },
        {
          text: this.$t("pdv"),
          value: "pdv",
          align: "right",
          width: "140px",
        }
      ]
    },
    restaurant() {
      return this.$store.state.restaurant || this.$store.state.company?.restaurant;
    }
  },
  methods: {
    print(withStock = false) {
      const { row, col, text, table, line, spacer, heading } = this.printElements;

      const docDate = this.localeDate(this.editedItem.document_date);
      const docNumber = this.editedItem.number || "";
      const docLabel = this.dialogTitle;

      const orderer = this.$store.state.restaurant || null;
      const user = this.$store.state.user.user;
      const supplier =
        this.fullSupplier ||
        this.editedItem.supplier ||
        (user.suppliers && user.suppliers[0]) ||
        null;

      this.printDoc(
        [
          row("between", [
            this.printCompanyBlock(orderer, this.$t("warehouse.orderer")),
            this.printCompanyBlock(supplier, this.$t("supplier")),
          ]),
          spacer(14),
          heading(docLabel + (docNumber ? " " + docNumber : "")),
          `<div style="text-align:center;font-size:11px;color:#777;margin-top:2px">${docDate}</div>`,
          spacer(12),
          table(this.getPrintHeaders(withStock), this.getPrintItems()),
          spacer(40),
          row("end", [
            col("center", [
              text(this.$t("supplier")),
              spacer(16),
              line(),
            ]),
          ]),
        ],
        { pageTitle: docLabel + (docNumber ? " " + docNumber : "") }
      );
    },
    async fetchSupplier() {
      const supplierId =
        this.editedItem?.supplier?.id || this.editedItem?.supplier_id;
      if (!supplierId) return;
      let suppliers = this.$store.state.warehouse.suppliers;
      if (!suppliers || !suppliers.length) {
        suppliers = await this.$store.dispatch("warehouse/fetchSuppliers");
      }
      this.fullSupplier = (suppliers || []).find((s) => s.id === supplierId) || null;
    },
    async exportExcel() {
      const docNumber = this.editedItem.number || "";
      const title = `${this.dialogTitle}${docNumber ? " " + docNumber : ""}`.trim();
      await this.downloadExcel({
        fileName: `${title}.xlsx`,
        sheets: [
          {
            name: title.substring(0, 31),
            title,
            headers: this.getPrintHeaders(false, false),
            items: this.getPrintItems(false),
          },
        ],
      });
    },
    async save() {
      const params = {
        //...this.editedItem,
        restaurant_id: this.restaurant.id,
        parent_id: this.editedItem.id,
        document_date: this.editedItem.document_date,
        document_type: 'order',

        warehouse_id: this.editedItem.warehouse?.id,
        warehouse: this.editedItem.warehouse,
        supplier_id: this.editedItem.supplier?.id || null,
        groceries: this.editedItem.groceries.map((grocery) => ({
          grocery_id: grocery.id,
          warehouse_id: this.editedItem.warehouse?.id,
          name: grocery.name,
          code: grocery.code,
          supplier_code: grocery.supplier_code,
          measure: grocery.measure,
          amount: grocery.amount,
          gross_quantity: Number(grocery.gross_quantity) || 0,
          unit_price: Number(grocery.unit_price) || 0,
          transaction_type: this.editedItem.document_type,
        })),
      };
      //console.log("params", params)
      //return
      const request = warehouseService.addDocumentTransaction(params);

      try {
        this.loading = true;
        await this.handleRequest(request, {
          successMessage: this.isPurchaseOrder
            ? this.$t("purchase_order_created_successfully")
            : this.$t("request_quote_created_successfully"),
          errorMessagePrefix: this.$t("error"),
        });
        this.model = false;
        this.$emit("success");
      } catch (error) {
        console.error("Save operation failed:", error);
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    model(val) {
      if (val) {
        this.fullSupplier = null;
        this.fetchSupplier();
      }
    },
  },
};
</script>